import { CommonModule } from '@angular/common';
import { Component, ViewChild, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { Router, ActivatedRoute } from '@angular/router';
import { SnackbarService } from '../../../services/snackbar/snackbar.service';
import { GruposService } from '../../../services/grupo/grupos.service';
import { CadastrarGrupoDadosGerais } from './cadastrar-grupo-dados-gerais/cadastrar-grupo-dados-gerais';
import { CadastrarGrupoEmpresas } from './cadastrar-grupo-empresas/cadastrar-grupo-empresas';
import { AuthService } from '../../../services/auth/auth.service';
import { clearPaginas, pushPaginaAtiva } from '../../../shared/activePage';

@Component({
  selector: 'app-cadastrar-grupo',
  imports: [
    MatButtonModule,
    MatInputModule,
    MatSnackBarModule,
    CommonModule,
    ReactiveFormsModule,
    CadastrarGrupoDadosGerais,
    CadastrarGrupoEmpresas,
  ],
  templateUrl: './cadastrar-grupo.html',
  styleUrl: './cadastrar-grupo.scss'
})
export class CadastrarGrupo implements OnInit {
  CADASTRAR_DADOS_GERAIS = 'CADASTRAR_DADOS_GERAIS';
  LISTAR_EMPRESAS = 'LISTAR_EMPRESAS';
  CADASTRAR_EMPRESAS = 'CADASTRAR_EMPRESAS';
  activeView: string = this.CADASTRAR_DADOS_GERAIS;

  grupoForm: FormGroup;
  grupoSalvo: any = null;

  @ViewChild(CadastrarGrupoEmpresas) cadastrarGrupoEmpresas!: CadastrarGrupoEmpresas;

  isCadastrarDadosGeraisActive(): boolean {
    return this.activeView === this.CADASTRAR_DADOS_GERAIS;
  }

  isListarEmpresasActive(): boolean {
    return this.activeView === this.LISTAR_EMPRESAS;
  }

  isCadastrarEmpresasActive(): boolean {
    return this.activeView === this.LISTAR_EMPRESAS;
  }

  isEmpresasTabDisabled(): boolean {
    return !this.grupoSalvo || !this.grupoSalvo.id;
  }

  onFormDataChange(formData: any) {
    this.grupoForm.patchValue(formData);
    // Força a validação do formulário após a atualização
    this.grupoForm.markAsTouched();
    this.grupoForm.updateValueAndValidity();
  }

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private snackBar: MatSnackBar,
    private snackBarService: SnackbarService,
    private gruposService: GruposService,
    private authService: AuthService
  ) {
    this.grupoForm = this.fb.group({
      nomeGrupo: ['', [Validators.required]],
      nomeCompleto: ['', [Validators.required]],
      email: ['', [Validators.required, Validators.email]],
      cargo: ['', [Validators.required]],
      telefoneCelular: ['', [Validators.required]],
    });
  }

  async ngOnInit() {
    clearPaginas();
    pushPaginaAtiva({
        nome: 'Home',
        rota: '/home'
    });
    pushPaginaAtiva({
        nome: 'Grupo de empresas',
        rota: '/grupos'
    });
    pushPaginaAtiva({
        nome: 'Cadastrar grupo de empresas',
        rota: ''
    });
    // Verifica se está em modo de criação e usuário não é MASTER
    const grupoId = this.route.snapshot.paramMap.get('id');
    if (!grupoId && !this.authService.isMasterUser()) {
      this.snackBarService.error('Apenas usuários MASTER podem criar grupos');
      this.router.navigate(['/grupos']);
      return;
    }

    // Se está em modo de edição, carrega os dados (permite visualização para todos)
    if (grupoId) {
      await this.carregarGrupoParaEdicao(parseInt(grupoId, 10), false);
    }
  }

  async carregarGrupoParaEdicao(grupoId: number, redirectToEmpresas: boolean = false) {
    try {
      const grupoDetalhado = await this.gruposService.buscarGrupoPorId(grupoId);
      this.grupoSalvo = grupoDetalhado;

      // Popula o formulário com os dados existentes
      if (grupoDetalhado.contatos && grupoDetalhado.contatos.length > 0) {
        const contato = grupoDetalhado.contatos[0];
        this.grupoForm.patchValue({
          nomeGrupo: grupoDetalhado.nome,
          nomeCompleto: contato.nome,
          email: contato.email,
          cargo: contato.cargo,
          telefoneCelular: contato.telefone,
        });
      }

      // Inicia na aba de empresas apenas se especificado
      if (redirectToEmpresas) {
        this.activeView = this.LISTAR_EMPRESAS;
      }
    } catch (error) {
      console.error('Erro ao carregar grupo para edição:', error);
      this.snackBarService.error('Erro ao carregar dados do grupo');
      if (redirectToEmpresas) {
        this.router.navigate(['/grupos']);
      }
    }
  }

  async chanceActiveScreen(newView: string) {
    // Se está tentando ir para a aba de empresas, valida se os dados gerais foram salvos
    if (newView === this.LISTAR_EMPRESAS && (!this.grupoSalvo || !this.grupoSalvo.id)) {
      this.snackBarService.error('Salve os dados gerais antes de vincular empresas');
      return;
    }

    // Se está voltando para dados gerais e já tem um grupo salvo, carrega os dados detalhados
    if (newView === this.CADASTRAR_DADOS_GERAIS && this.grupoSalvo && this.grupoSalvo.id) {
      await this.carregarDadosGrupo();
    }

    this.activeView = newView;
  }

  async carregarDadosGrupo() {
    if (this.grupoSalvo && this.grupoSalvo.id) {
      await this.carregarGrupoParaEdicao(this.grupoSalvo.id);
    }
  }

  adicionarEmpresa() {
    if (this.cadastrarGrupoEmpresas && this.grupoSalvo?.id) {
      this.cadastrarGrupoEmpresas.adicionarEmpresas();
    } else {
      this.snackBarService.error('Salve os dados gerais antes de adicionar empresas');
    }
  }

  getGrupoData() {
    const formValue = this.grupoForm.value;

    return {
      nome: formValue.nomeGrupo,
      contato: {
        nome: formValue.nomeCompleto,
        email: formValue.email,
        cargo: formValue.cargo,
        telefone: formValue.telefoneCelular,
      },
    };
  }

  getUpdateGrupoData() {
    const formValue = this.grupoForm.value;

    return {
      idGrupo: this.grupoSalvo.id,
      nome: formValue.nomeGrupo,
      contato: {
        idContato: this.grupoSalvo.contatos && this.grupoSalvo.contatos.length > 0 ? this.grupoSalvo.contatos[0].id : null,
        nome: formValue.nomeCompleto,
        email: formValue.email,
        cargo: formValue.cargo,
        telefone: formValue.telefoneCelular,
      },
    };
  }

  async saveDadosGerais() {
    // Verifica se usuário tem permissão para salvar
    if (!this.authService.isMasterUser()) {
      this.snackBarService.error('Apenas usuários MASTER podem editar grupos');
      return;
    }

    if (this.grupoForm.invalid) {
      this.snackBarService.error(
        'Por favor, preencha todos os campos obrigatórios.'
      );
      return;
    }

    try {
      const isUpdate = this.grupoSalvo && this.grupoSalvo.id;

      if (isUpdate) {
        // Atualização
        this.grupoSalvo = await this.gruposService.atualizarGrupo(this.getUpdateGrupoData());
        this.snackBarService.success('Dados atualizados com sucesso!');
      } else {
        // Criação
        this.grupoSalvo = await this.gruposService.criarGrupo(this.getGrupoData());
        this.grupoForm.patchValue({ idGrupo: this.grupoSalvo.id });
        this.snackBarService.success('Dados salvos com sucesso!');
      }

      // Muda automaticamente para a aba de empresas sempre após salvar
      this.activeView = this.LISTAR_EMPRESAS;
    } catch (err: any) {
      console.error(err);
      this.snackBarService.error(
        err.response?.data?.message || 'Erro ao salvar dados'
      );
    }
  }

  isMasterUser(): boolean {
    return this.authService.isMasterUser();
  }

  isReadOnlyMode(): boolean {
    return !this.authService.isMasterUser();
  }
}
